/**
 * GO Lab Collection Detail page.
 *
 * Mounted at `/v4/go-lab/datasets/:collectionId/:tab`. Renders breadcrumb
 * back to the home grid, a header molecule, and a six-tab structure
 * (`datasets | split | overview | coverage | quality | upload`). The active
 * tab is driven by the `:tab` URL segment (so a refresh / shared link keeps
 * the tab), mirroring the Annotate / Intelligence URL-segment nav.
 */

import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ErrorBoundary } from '../../../ui/error-boundary';
import { Button } from '../../../ui/button';
import SegmentedNav from '../../../ui/segmented-nav';
import { TooltipProvider } from '../../../ui/tooltip';

import { APIErrorException } from '../../../../services/types/errors';
import { useTaxonomyKeys } from '../hooks/useTaxonomy';
import CollectionHeader from './components/detail/CollectionHeader';
import DeleteCollectionModal from './components/detail/DeleteCollectionModal';
import EditCollectionModal from './components/detail/EditCollectionModal';
import StatusTransitionModal from './components/detail/StatusTransitionModal';
import DatasetsTabContent from './components/datasets-tab/DatasetsTabContent';
import SplitTab from './SplitTab';
import UploadWizard from './UploadWizard/UploadWizard';
import OverviewTab from './OverviewTab';
import CoverageTab from './CoverageTab';
import QualityTab from './QualityTab';
import {
  useActivateCollection,
  useCollection,
  useDeactivateCollection,
} from './hooks/useDatasets';

const TAB_VALUES = ['datasets', 'split', 'overview', 'coverage', 'quality', 'upload'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const TAB_LABELS: Record<TabValue, string> = {
  datasets: 'Datasets',
  split: 'Train / Eval Split',
  overview: 'Overview',
  coverage: 'Coverage',
  quality: 'Quality',
  upload: 'Upload & Configure',
};

const wrapperClassName =
  'flex h-full flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6';

/**
 * Monitor-style inline breadcrumb (matches `/v4/records` and the
 * annotate flow pages). Parent link → "/" separator → current page
 * label with `aria-current="page"` for screen readers + the existing
 * test that finds the trailing crumb via that selector.
 */
const Crumbs: React.FC<{ trailing?: string }> = ({ trailing }) => (
  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
    <Link
      to="/v4/go-lab/datasets"
      className="font-medium text-primary hover:underline"
    >
      Datasets
    </Link>
    {trailing && (
      <>
        <span aria-hidden>/</span>
        <span aria-current="page">{trailing}</span>
      </>
    )}
  </div>
);

const CollectionDetailPage: React.FC = () => {
  const { collectionId = '', tab } = useParams<{ collectionId: string; tab: string }>();
  const navigate = useNavigate();
  // The active tab lives in the URL (`:tab`); fall back to `datasets` for a
  // missing/unknown segment (the bare `:collectionId` URL is redirected to
  // the default tab at the route level).
  const activeTab: TabValue = (TAB_VALUES as readonly string[]).includes(tab ?? '')
    ? (tab as TabValue)
    : 'datasets';
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: collection, isLoading, isError, error, refetch } = useCollection(collectionId);
  const { data: taxonomyKeys } = useTaxonomyKeys();
  const activate = useActivateCollection(collectionId);
  const deactivate = useDeactivateCollection(collectionId);

  const taxonomyDisplayName = useMemo(() => {
    if (!collection) return '';
    const match = (taxonomyKeys || []).find((k) => k.taxonomyKey === collection.taxonomyKey);
    return match?.displayName ?? collection.taxonomyKey;
  }, [collection, taxonomyKeys]);

  if (isLoading) {
    return (
      <div className={wrapperClassName}>
        <div className="space-y-6" data-testid="collection-detail-loading">
          <Crumbs />
          <div className="h-12 w-2/3 animate-pulse rounded bg-muted" />
          <div className="h-10 w-full animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (isError) {
    const isNotFound = error instanceof APIErrorException && error.status === 404;
    return (
      <div className={wrapperClassName}>
        <div className="space-y-6">
          <Crumbs />
          <div className="flex h-[40vh] flex-col items-center justify-center text-center">
            <p className="mb-4 text-muted-foreground">
              {isNotFound ? 'Collection not found.' : 'Failed to load collection.'}
            </p>
            {isNotFound ? (
              <Button variant="link" size="sm" onClick={() => navigate('/v4/go-lab/datasets')}>
                Back to Datasets
              </Button>
            ) : (
              <Button variant="link" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!collection) {
    // react-query without an error and without data is unusual; bail safely.
    return null;
  }

  const statusMutation = collection.status === 'active' ? deactivate : activate;
  const handleStatusConfirm = () => {
    statusMutation.mutate(undefined, {
      onSuccess: () => setStatusModalOpen(false),
    });
  };

  return (
    <div className={wrapperClassName}>
      <TooltipProvider>
        <div className="space-y-6">
          <Crumbs trailing={collection.name} />

          <CollectionHeader
            name={collection.name}
            region={collection.region}
            industry={collection.industry}
            taxonomyDisplayName={taxonomyDisplayName}
            status={collection.status}
            onStatusClick={() => setStatusModalOpen(true)}
            onEdit={() => setEditModalOpen(true)}
            onDelete={() => setDeleteModalOpen(true)}
          />

          <SegmentedNav
            ariaLabel="Collection sections"
            items={TAB_VALUES.map((v) => ({
              key: v,
              label: TAB_LABELS[v],
              href: `/v4/go-lab/datasets/${collectionId}/${v}`,
              matchPaths: [`/v4/go-lab/datasets/${collectionId}/${v}`],
            }))}
          />
          <div>
            {activeTab === 'datasets' ? (
              <DatasetsTabContent
                collection={collection}
                onSwitchTab={(next) =>
                  navigate(`/v4/go-lab/datasets/${collectionId}/${next}`)
                }
              />
            ) : activeTab === 'split' ? (
              <SplitTab collection={collection} />
            ) : activeTab === 'upload' ? (
              <UploadWizard
                collection={collection}
                onComplete={() =>
                  navigate(`/v4/go-lab/datasets/${collectionId}/datasets`)
                }
              />
            ) : activeTab === 'overview' ? (
              <OverviewTab collection={collection} />
            ) : activeTab === 'coverage' ? (
              <CoverageTab collection={collection} />
            ) : (
              <QualityTab collection={collection} />
            )}
          </div>
        </div>
        <StatusTransitionModal
          open={statusModalOpen}
          onOpenChange={setStatusModalOpen}
          currentStatus={collection.status}
          onConfirm={handleStatusConfirm}
          isPending={statusMutation.isPending}
        />
        <EditCollectionModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          collection={collection}
        />
        <DeleteCollectionModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          collection={collection}
          onDeleted={() => navigate('/v4/go-lab/datasets')}
        />
      </TooltipProvider>
    </div>
  );
};

const CollectionDetailPageWithBoundary: React.FC = () => (
  <ErrorBoundary>
    <CollectionDetailPage />
  </ErrorBoundary>
);

export default CollectionDetailPageWithBoundary;
